var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, FieldSelect, FieldText, FlexBox, ModalBody, ModalFoot, ModalHead, Spinner, } from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import differenceWith from 'lodash/differenceWith';
import isEqual from 'lodash/isEqual';
import moment from 'moment-timezone';
import { Component } from 'react';
import { Field, FieldArray, Form } from 'redux-form';
import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import { getRequestID } from 'components/shared/util/request';
import { FieldRepeater, RepeaterDivider } from 'fable/fieldRepeater';
import { getSubmitAnalyticsAction } from 'utils/flowEditor/analytics';
import { SystemVariableId, SystemVariableType, } from '../../helpers/flowVariableHelpers';
import { FlowNodeTypes, MatchingOperator, MatchingStrategy } from '../types';
import styles from './filters.module.scss';
import { CriteriaFields, MatchIdType } from './filters/criteriaFields';
var FormStatus;
(function (FormStatus) {
    FormStatus["Initial"] = "init";
    FormStatus["Mounting"] = "mounting";
    FormStatus["Mounted"] = "mounted";
})(FormStatus || (FormStatus = {}));
export class Filters extends Component {
    constructor() {
        super(...arguments);
        this.state = {
            formStatus: FormStatus.Initial,
            hasError: false,
        };
    }
    static getDerivedStateFromError() {
        // Update state so the next render will show the fallback UI.
        return { hasError: true };
    }
    componentDidMount() {
        setTimeout(() => {
            this.setState({
                formStatus: FormStatus.Mounting,
            });
        });
    }
    onFormMounted() {
        this.setState({
            formStatus: FormStatus.Mounted,
        });
    }
    componentDidCatch(error, errorInfo) {
        // You can also log the error to an error reporting service
        console.log(error, errorInfo);
    }
    handleTriggerEvent(rules, initialRules) {
        const { flowType, node } = this.props;
        const addedRules = differenceWith(rules, initialRules, isEqual).map(({ variable }) => this.getVariableObj(variable).id);
        const removedRules = node.nodeType === FlowNodeTypes.FILTERS
            ? differenceWith(initialRules, rules, isEqual).map(({ variable }) => this.getVariableObj(variable).id)
            : [];
        const analyticsEvent = getSubmitAnalyticsAction(!!node.isAction);
        analyticsEvent({
            flowType: flowType,
            nodeType: FlowNodeTypes.FILTERS,
            settings: Object.assign(Object.assign({}, (addedRules.length && { added: addedRules })), (removedRules.length && { removed: removedRules })),
        });
    }
    saveClickHandler(values) {
        const { initialValues: { rules: initialRules }, replaceNode, triggerClose, } = this.props;
        this.handleTriggerEvent(values.rules, initialRules);
        replaceNode(this._prepareNode(values));
        triggerClose();
    }
    getLocalDateString(date, timezone) {
        const localDateObject = moment.utc(date, moment.ISO_8601).tz(timezone);
        const isValidISOString = moment(localDateObject.toISOString(), moment.ISO_8601).isValid();
        return isValidISOString ? localDateObject.format() : null;
    }
    getVariableObj(value = '', variableType = '') {
        // type guard for DateObject
        function isValidDateObject(dateObject) {
            return dateObject.date !== undefined;
        }
        // type guard for DateRangeObject
        function isValidDateRangeObject(dateRangeObject) {
            return (dateRangeObject.startDate !== undefined &&
                dateRangeObject.endDate !== undefined);
        }
        if (typeof value === 'string') {
            const variableRegex = /((scope|system):)?([^}]+)/;
            const match = variableRegex.exec(value);
            if (match !== null && match[2] && match[3]) {
                return { type: match[2], id: match[3] };
            }
        }
        if (typeof value === 'object' &&
            variableType === MatchIdType.Date &&
            isValidDateObject(value) &&
            value.timezone) {
            const localDateISOString = this.getLocalDateString(value.date, value.timezone);
            if (localDateISOString) {
                return {
                    type: SystemVariableType.Input,
                    id: SystemVariableId.Date,
                    value: `${localDateISOString}[${value.timezone}]`,
                };
            }
        }
        if (typeof value === 'object' &&
            variableType === MatchIdType.DateRange &&
            isValidDateRangeObject(value) &&
            value.timezone) {
            const localStartDateISOString = this.getLocalDateString(value.startDate, value.timezone);
            const localEndDateISOString = this.getLocalDateString(value.endDate, value.timezone);
            return {
                type: SystemVariableType.Input,
                id: SystemVariableId.DateRange,
                value: {
                    start_date: `${localStartDateISOString}[${value.timezone}]`,
                    end_date: `${localEndDateISOString}[${value.timezone}]`,
                },
            };
        }
        if (typeof value === 'number' &&
            variableType === MatchIdType.SpamProbabilityVariable) {
            return {
                type: SystemVariableType.Input,
                id: SystemVariableId.SpamProbability,
                value: value * 0.01, // Convert from the range input values (1-100) to the 0 to 1 value the service expects.
            };
        }
        return {
            type: SystemVariableType.Input,
            value: typeof value === 'string' ? value : '',
            id: '',
        };
    }
    _prepareNode(values) {
        var _a;
        const { node } = this.props;
        const { payload } = node;
        const matching_strategy = values.matching_strategy || MatchingStrategy.ANY;
        const description = (_a = values.description) !== null && _a !== void 0 ? _a : '';
        const rules = values.rules.map((rule) => {
            return {
                matching_rule: rule.matching_rule || MatchingOperator.EQUALS,
                variable: this.getVariableObj(rule.variable),
                match: this.getVariableObj(rule.match, rule.variableType),
            };
        });
        return Object.assign(Object.assign({}, node), { badges: [], icon: 'i-bug', isAction: false, isEditable: true, isRemovable: true, nodeType: FlowNodeTypes.FILTERS, overlay: [], payload: {
                matching_strategy,
                rules,
                match_node_id: (payload === null || payload === void 0 ? void 0 : payload.match_node_id) || getRequestID(),
                non_match_node_id: (payload === null || payload === void 0 ? void 0 : payload.non_match_node_id) || getRequestID(),
                description,
            }, text: 'Branch' });
    }
    render() {
        const { changeFormField, flowType, handleSubmit, matching_strategy, node, nodeLabel, pristine, rules, scopeVars, triggerClose, } = this.props;
        const { formStatus } = this.state;
        const [lastRule] = [...rules].reverse();
        const isLastRuleSpamProbability = this.getVariableObj(lastRule.variable).id === 'spam_probability';
        return (_jsxs(Box, Object.assign({ width: "600px" }, { children: [_jsx(ModalHead, Object.assign({ title: nodeLabel, onClose: triggerClose }, { children: _jsx("div", Object.assign({ className: styles.description }, { children: "Sort conversations based on whether they match your chosen criteria." })) })), formStatus !== FormStatus.Mounted && (_jsx(FlexBox, Object.assign({ justifyContent: "center", alignItems: "center" }, { children: _jsx(Spinner, {}) }))), formStatus !== FormStatus.Initial && (_jsxs(Form, Object.assign({ onSubmit: handleSubmit(this.saveClickHandler) }, { children: [_jsxs(ModalBody, { children: [_jsx(Field, { label: "Add a description", name: "description", component: FieldText, placeholder: "The purpose of this filter", required: true }), _jsx(Field, { component: FieldSelect, name: "matching_strategy", label: "Filter conversation if:", items: [
                                        {
                                            id: MatchingStrategy.ANY,
                                            name: 'One or more of the criteria are met',
                                        },
                                        {
                                            id: MatchingStrategy.ALL,
                                            name: 'All of the criteria are met',
                                        },
                                    ] }), _jsx(FieldArray, { buttonProps: { placement: 'outside', label: 'New criteria' }, className: styles.fieldRepeaterWrapper, component: FieldRepeater, defaultRowProps: Object.assign(Object.assign({}, lastRule), { match: isLastRuleSpamProbability ? 50 : '', variableType: isLastRuleSpamProbability
                                            ? MatchIdType.SpamProbabilityVariable
                                            : MatchIdType.Input }), divider: _jsx(RepeaterDivider, { label: matching_strategy === MatchingStrategy.ALL ? 'AND' : 'OR' }), name: "rules", onMount: this.onFormMounted, rowComponent: CriteriaFields, rowProps: {
                                        flowType,
                                        rules,
                                        scopeVariables: scopeVars,
                                        changeFormField,
                                    }, title: "Criteria" })] }), _jsx(ModalFoot, { cancelOnClick: triggerClose, confirmName: getConfirmButtonVerb(node), confirmOnClick: handleSubmit(this.saveClickHandler), confirmProps: { disabled: pristine } })] })))] })));
    }
}
__decorate([
    boundMethod
], Filters.prototype, "onFormMounted", null);
__decorate([
    boundMethod
], Filters.prototype, "handleTriggerEvent", null);
__decorate([
    boundMethod
], Filters.prototype, "saveClickHandler", null);
__decorate([
    boundMethod
], Filters.prototype, "getLocalDateString", null);
__decorate([
    boundMethod
], Filters.prototype, "_prepareNode", null);
